import { useCallback, useEffect, useState } from 'react';
import type { AppConfig } from '../config.js';
import { apiFetch } from '../api/client.js';

/**
 * Who can see this log.
 *
 * The point of the phase: adding Richard's doctor with read-and-export only, or taking
 * someone's access away, without anyone opening an AWS console.
 *
 * Permissions are presented as plain consequences rather than enum names. "Manage who
 * has access" is a decision a person can make; MANAGE_CARE_TEAM is not.
 */

interface Member {
  userId: string;
  email: string;
  relationship?: string;
  permissions: string[];
  status: string;
  revokedAt: string | null;
}

interface Invitation {
  email: string;
  displayName: string;
  permissions: string[];
  expiresAt: string;
  expired: boolean;
}

const PERMISSIONS: { key: string; label: string; hint: string }[] = [
  { key: 'VIEW_LOG', label: 'See the log', hint: 'doses, symptoms and times' },
  { key: 'LOG_ON_BEHALF', label: 'Record entries', hint: 'log a dose on his behalf' },
  { key: 'RECEIVE_ALERTS', label: 'Receive alerts', hint: 'missed doses and symptoms' },
  { key: 'EXPORT_REPORT', label: 'Export reports', hint: 'the PDF for a doctor' },
  { key: 'MANAGE_REGIMEN', label: 'Edit the medicines', hint: 'change the schedule' },
  { key: 'MANAGE_CARE_TEAM', label: 'Manage access', hint: 'invite and remove people' },
];

/** What a doctor needs and nothing more — the common case, one tap. */
const DOCTOR_PRESET = ['VIEW_LOG', 'EXPORT_REPORT'];
const FAMILY_PRESET = ['VIEW_LOG', 'LOG_ON_BEHALF', 'RECEIVE_ALERTS', 'EXPORT_REPORT'];

export function CareTeam({
  config,
  patientId,
  patientName,
  onBack,
}: {
  config: AppConfig;
  patientId: string;
  patientName: string;
  onBack: () => void;
}) {
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>(FAMILY_PRESET);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ members: Member[]; invitations: Invitation[] }>(
        config,
        `/v1/patients/${encodeURIComponent(patientId)}/care-team`,
      );
      setMembers(data.members);
      setInvitations(data.invitations);
    } catch {
      setError('Could not load the care team.');
    } finally {
      setLoading(false);
    }
  }, [config, patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function sendInvite(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await apiFetch(config, `/v1/patients/${encodeURIComponent(patientId)}/care-invites`, {
        method: 'POST',
        body: { email: email.trim(), displayName: name.trim(), permissions: chosen },
      });
      setEmail('');
      setName('');
      setInviting(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the invitation.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(member: Member): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await apiFetch(
        config,
        `/v1/patients/${encodeURIComponent(patientId)}/care-team/${encodeURIComponent(member.userId)}`,
        { method: 'DELETE' },
      );
      await load();
    } catch {
      setError('Could not remove that person.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <main className="app">
        <p className="muted">Loading…</p>
      </main>
    );
  }

  if (inviting) {
    return (
      <main className="app">
        <h1>Invite someone</h1>
        <p className="muted">
          They will get an email with a link. They must sign in with this exact address —
          forwarding the link to anyone else will not work.
        </p>

        <div className="stack">
          <input
            className="field"
            type="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            placeholder="their email"
            aria-label="Their email address"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
          <input
            className="field"
            placeholder="their name"
            aria-label="Their name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </div>

        <h2>What should they be able to do?</h2>
        <div className="chips">
          <button
            type="button"
            className={`chip ${sameSet(chosen, FAMILY_PRESET) ? 'on' : ''}`}
            onClick={() => {
              setChosen(FAMILY_PRESET);
            }}
          >
            Family member
          </button>
          <button
            type="button"
            className={`chip ${sameSet(chosen, DOCTOR_PRESET) ? 'on' : ''}`}
            onClick={() => {
              setChosen(DOCTOR_PRESET);
            }}
          >
            Doctor — read only
          </button>
        </div>

        {PERMISSIONS.map((p) => (
          <label className="perm" key={p.key}>
            <input
              type="checkbox"
              checked={chosen.includes(p.key)}
              onChange={() => {
                setChosen((prev) =>
                  prev.includes(p.key) ? prev.filter((k) => k !== p.key) : [...prev, p.key],
                );
              }}
            />
            <span>
              <strong>{p.label}</strong>
              <span className="muted"> — {p.hint}</span>
            </span>
          </label>
        ))}

        {error !== undefined && <p className="error">{error}</p>}

        <div className="btn-row">
          <button
            className="btn-primary"
            disabled={busy || email.trim().length < 3 || name.trim().length === 0 || chosen.length === 0}
            onClick={() => void sendInvite()}
          >
            {busy ? 'Sending…' : 'Send the invitation'}
          </button>
          <button
            className="btn-quiet"
            disabled={busy}
            onClick={() => {
              setInviting(false);
              setError(undefined);
            }}
          >
            Cancel
          </button>
        </div>
      </main>
    );
  }

  const active = members.filter((m) => m.revokedAt == null);
  const removed = members.filter((m) => m.revokedAt != null);

  return (
    <main className="app">
      <h1>Who can see this</h1>
      <p className="muted">{patientName}&rsquo;s medication log</p>

      {active.map((member) => (
        <section className="card" key={member.userId}>
          <div className="card-head">
            <span className="med">{member.email}</span>
          </div>
          <div className="slot">
            {member.permissions.map((p) => labelFor(p)).join(' · ')}
          </div>
          <button
            className="btn-quiet undo"
            disabled={busy}
            onClick={() => void revoke(member)}
          >
            Remove access
          </button>
        </section>
      ))}

      {invitations.length > 0 && <h2>Invited, not yet accepted</h2>}
      {invitations.map((invite) => (
        <section className="card" key={invite.email}>
          <div className="card-head">
            <span className="med">{invite.email}</span>
            <span className="when muted">{invite.expired ? 'expired' : 'pending'}</span>
          </div>
          <div className="slot">{invite.permissions.map((p) => labelFor(p)).join(' · ')}</div>
        </section>
      ))}

      {/* Kept for the record rather than hidden: a chart should show that access
          existed and when it ended. */}
      {removed.length > 0 && <h2>No longer has access</h2>}
      {removed.map((member) => (
        <section className="card" key={member.userId}>
          <div className="card-head">
            <span className="med">{member.email}</span>
            <span className="when muted">removed</span>
          </div>
        </section>
      ))}

      {error !== undefined && <p className="error">{error}</p>}

      <div className="btn-row">
        <button
          className="btn-primary"
          onClick={() => {
            setInviting(true);
          }}
        >
          Invite someone
        </button>
        <button className="btn-quiet" onClick={onBack}>
          Back
        </button>
      </div>
    </main>
  );
}

function labelFor(permission: string): string {
  return PERMISSIONS.find((p) => p.key === permission)?.label ?? permission;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}
